import api from './api';

export type StatutEntreprise = 'active' | 'suspendue' | 'expiree';
export type PlanEntreprise = 'basique' | 'pro' | 'premium';
export type StatutLicence = 'active' | 'expiree' | 'suspendue' | 'revoquee';

export interface Entreprise {
  id: number;
  nom: string;
  slug: string;
  statut: StatutEntreprise;
  plan: PlanEntreprise;
  maxBoutiques: number;
  maxUtilisateurs: number;
  maxProduits: number;
  adresse?: string;
  telephone?: string;
  email?: string;
  ifu?: string;
  rccm?: string;
  createdAt: string;
}

export type StatutPaiement = 'paye' | 'impaye' | 'partiel';

export interface Licence {
  id: number;
  cle: string;
  entrepriseId: number;
  plan: PlanEntreprise;
  statut: StatutLicence;
  dateDebut: string;
  dateExpiration: string;
  montant: number;
  statutPaiement: StatutPaiement;
  datePaiement: string | null;
  notes?: string;
  createdAt: string;
}

export interface EntreprisesStats {
  total: number;
  actives: number;
  suspendues: number;
  expirees: number;
}

export interface UtilisateurEntreprise {
  id: number;
  email: string;
  nom: string;
  prenom: string;
  role: string;
  actif: boolean;
  boutiqueNom: string | null;
  createdAt: string;
  derniereConnexion: string | null;
}

export interface UtilisationEntreprise {
  utilisateurs: { count: number; max: number };
  boutiques: { count: number; max: number };
  produits: { count: number; max: number };
}

export interface CreateAdminDto {
  email: string;
  password: string;
  nom?: string;
  prenom?: string;
}

export interface CreateEntrepriseDto {
  nom: string;
  email?: string;
  telephone?: string;
  adresse?: string;
  ifu?: string;
  rccm?: string;
  // Compte administrateur initial (création uniquement).
  adminEmail?: string;
  adminPassword?: string;
  adminNom?: string;
  adminPrenom?: string;
}

export interface CreateLicenceDto {
  entrepriseId: number;
  plan: PlanEntreprise;
  dureeMois?: number;
  dureeHeures?: number;
  montant?: number;
  statutPaiement?: StatutPaiement;
  notes?: string;
}

export interface ActiviteEntreprise {
  entrepriseId: number;
  nom: string;
  statut: StatutEntreprise;
  derniereConnexion: string | null;
  derniereVente: string | null;
  nbVentes30j: number;
  ca30j: number;
  nbVentesTotal: number;
  caTotal: number;
}

export interface Sauvegarde {
  nom: string;
  taille: string;
  date: string;
  type: 'automatique' | 'manuelle';
}

export interface AuditLog {
  id: number;
  action: string;
  acteurEmail: string | null;
  cibleType: string | null;
  cibleId: number | null;
  cibleLabel: string | null;
  details: string | null;
  ip: string | null;
  createdAt: string;
}

export const superadminService = {
  // --- Entreprises ---
  listerEntreprises(): Promise<Entreprise[]> {
    return api.get('/entreprises').then((r) => r.data);
  },
  statsEntreprises(): Promise<EntreprisesStats> {
    return api.get('/entreprises/stats').then((r) => r.data);
  },
  creerEntreprise(data: CreateEntrepriseDto): Promise<Entreprise> {
    return api.post('/entreprises', data).then((r) => r.data);
  },
  modifierEntreprise(id: number, data: Partial<CreateEntrepriseDto & { statut: StatutEntreprise }>): Promise<Entreprise> {
    return api.put(`/entreprises/${id}`, data).then((r) => r.data);
  },
  utilisateursEntreprise(id: number): Promise<UtilisateurEntreprise[]> {
    return api.get(`/entreprises/${id}/utilisateurs`).then((r) => r.data);
  },
  utilisationEntreprise(id: number): Promise<UtilisationEntreprise> {
    return api.get(`/entreprises/${id}/utilisation`).then((r) => r.data);
  },
  creerAdmin(id: number, data: CreateAdminDto): Promise<UtilisateurEntreprise> {
    return api.post(`/entreprises/${id}/admin`, data).then((r) => r.data);
  },
  resetMotDePasse(id: number, userId: number, password: string): Promise<void> {
    return api.post(`/entreprises/${id}/utilisateurs/${userId}/reset-password`, { password }).then((r) => r.data);
  },
  /** Suppression DÉFINITIVE d'une entreprise et de toutes ses données (irréversible). */
  supprimerEntreprise(id: number): Promise<Record<string, number>> {
    return api.delete(`/entreprises/${id}`).then((r) => r.data);
  },

  // --- Licences ---
  listerLicences(): Promise<Licence[]> {
    return api.get('/licences').then((r) => r.data);
  },
  licencesEntreprise(entrepriseId: number): Promise<Licence[]> {
    return api.get(`/licences/entreprise/${entrepriseId}`).then((r) => r.data);
  },
  emettreLicence(data: CreateLicenceDto): Promise<Licence> {
    return api.post('/licences', data).then((r) => r.data);
  },
  renouvelerLicence(
    id: number,
    dureeMois = 12,
    montant?: number,
    statutPaiement?: StatutPaiement,
    dureeHeures?: number,
  ): Promise<Licence> {
    return api
      .post(`/licences/${id}/renouveler`, { dureeMois, montant, statutPaiement, dureeHeures })
      .then((r) => r.data);
  },
  marquerPaiement(id: number, statutPaiement: StatutPaiement): Promise<Licence> {
    return api.put(`/licences/${id}/paiement`, { statutPaiement }).then((r) => r.data);
  },
  revoquerLicence(id: number): Promise<Licence> {
    return api.post(`/licences/${id}/revoquer`).then((r) => r.data);
  },

  // --- Activité des tenants ---
  listerActivite(): Promise<ActiviteEntreprise[]> {
    return api.get('/entreprises/activite').then((r) => r.data);
  },

  // --- Journal d'audit ---
  listerAudit(limite = 200): Promise<AuditLog[]> {
    return api.get('/audit', { params: { limite } }).then((r) => r.data);
  },

  // --- Sauvegardes (dump global : réservé au super-admin) ---
  listerSauvegardes(): Promise<Sauvegarde[]> {
    return api.get('/sauvegarde').then((r) => r.data);
  },
  dossierSauvegardes(): Promise<{ dossier: string }> {
    return api.get('/sauvegarde/dossier').then((r) => r.data);
  },
  lancerSauvegarde(): Promise<{ succes: boolean; message: string; fichier?: string }> {
    return api.post('/sauvegarde/lancer').then((r) => r.data);
  },

  // --- E-mail / alertes ---
  statutMail(): Promise<{ actif: boolean; emetteur: string | null; destinataireAlertes: string | null }> {
    return api.get('/mail/status').then((r) => r.data);
  },
  testerMail(to?: string): Promise<{ ok: boolean; erreur?: string; destinataire?: string }> {
    return api.post('/mail/test', { to }).then((r) => r.data);
  },
};
